import React, { useState } from 'react';
import { CloudArrowUpIcon, CalendarIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import CPEComplianceError from './CPEComplianceError';
import { SCRUBBER_API_BASE } from '../api/config';
import { csrfFetch } from '../api/csrf';

export default function CPEFileUploadPanel({ onFileUpload, onValidationComplete, selectedTable, onUniversePeriodChange }) {
  const [isUploading, setIsUploading] = useState(false);
  const [engagementDate, setEngagementDate] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const [period, setPeriod] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [complianceError, setComplianceError] = useState(null);

  // Format date as YYYY/MM/DD for display consistency
  const formatDate = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}/${m}/${d}`;
  };

  // When engagement date changes, compute the 26-week window and notify parent
  const handleEngagementDateChange = (value) => {
    setEngagementDate(value);
    if (!value) {
      setPeriod(null);
      if (onUniversePeriodChange) onUniversePeriodChange(null);
      return;
    }
    const end = new Date(value);
    if (isNaN(end.getTime())) return;
    const start = new Date(end);
    start.setDate(start.getDate() - (26 * 7)); // 26-week period preceding and including engagement date
    const computed = { startDate: formatDate(start), endDate: formatDate(end) };
    setPeriod(computed);
    if (onUniversePeriodChange) onUniversePeriodChange(computed);
  };

  const handleFileChange = (file) => {
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileChange(e.dataTransfer.files[0]);
    }
  };

  const processFile = async (file) => {
    if (!engagementDate) {
      alert('Please enter an engagement date before uploading');
      return;
    }
    if (!file) {
      alert('Please choose a file to upload');
      return;
    }

    // Validate file type
    const fileExtension = file.name.split('.').pop().toLowerCase();
    if (!['xlsx', 'txt'].includes(fileExtension)) {
      alert('Please upload a .xlsx or .txt file');
      return;
    }

    setIsUploading(true);
    setComplianceError(null);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('engagementDate', engagementDate);

      const response = await csrfFetch(`${SCRUBBER_API_BASE}/api/validate/cpe-coa`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Upload failed');
      }

      const result = await response.json();
      
      // Check for header compliance errors
      const headerErrors = result.errors?.filter(err => 
        err.field === 'COMPLIANCE REQUIREMENT' || 
        err.category === 'Missing Data' || 
        err.category === 'Formatting Issues'
      ) || [];
      
      if (headerErrors.length > 0 && headerErrors.some(err => 
        err.message.includes('CMS field') || 
        err.message.includes('compliance') ||
        err.field === 'COMPLIANCE REQUIREMENT'
      )) {
        setComplianceError({
          headerErrors,
          actualHeaders: null // We could extract this from server logs if needed
        });
        return; // Don't proceed with normal flow
      }
      
      // Debug logging
      console.log('🛠 DEBUG — CPE validation result:', {
        success: result.success,
        universe: !!result.universe,
        universeLength: result.universe?.length,
        errors: !!result.errors,
        errorsLength: result.errors?.length,
        validation: !!result.validation
      });
      
      if (onFileUpload) onFileUpload(file);
      if (onValidationComplete) {
        onValidationComplete(result);
      }

    } catch (error) {
      console.error('Upload error:', error);
      alert(`Upload failed: ${error.message}`);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="w-full max-w-4xl mx-auto">
      {/* Show compliance error if header validation failed */}
      {complianceError && (
        <div className="mb-8">
          <CPEComplianceError 
            headerErrors={complianceError.headerErrors}
            actualHeaders={complianceError.actualHeaders}
          />
        </div>
      )}

      {/* Header */}
      <div className="text-center mb-8">
        <div className="inline-flex items-center gap-3 px-6 py-3 bg-emerald-50/90 backdrop-blur-xl rounded-2xl border border-emerald-200/60 shadow-lg mb-6">
          <DocumentTextIcon className="w-5 h-5 text-emerald-700" />
          <span className="text-emerald-700 font-bold">CPE COA Universe Upload</span>
        </div>
        <h2 className="text-2xl font-bold text-slate-900 mb-4">
          Upload Compliance Oversight Activities File
        </h2>
        <p className="text-slate-600 max-w-2xl mx-auto">
          Upload your CPE COA universe file (.xlsx or .txt) with the engagement date to validate compliance oversight activities.
        </p>
      </div>

      {/* Engagement Date Input */}
      <div className="mb-8">
        <label className="block text-sm font-semibold text-slate-700 mb-3">
          <CalendarIcon className="w-4 h-4 inline mr-2" />
          Engagement Date (Required)
        </label>
        <input
          type="date"
          value={engagementDate}
          onChange={(e) => handleEngagementDateChange(e.target.value)}
          className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors"
          required
        />
        {period && (
          <div className="mt-3 inline-flex items-center gap-3 px-4 py-2 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm font-semibold">
            <CalendarIcon className="w-4 h-4" />
            <span>Universe Period:</span>
            <span className="font-bold">{period.startDate} → {period.endDate}</span>
          </div>
        )}
      </div>

      {/* File Upload Area */}
      <div
        className={`
          relative border-2 border-dashed rounded-2xl p-12 text-center transition-all duration-300
          ${dragActive 
            ? 'border-emerald-400 bg-emerald-50/50' 
            : 'border-slate-300 hover:border-emerald-400 hover:bg-emerald-50/30'
          }
          ${isUploading ? 'opacity-50 pointer-events-none' : ''}
        `}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <input
          type="file"
          accept=".xlsx,.txt"
          onChange={(e) => handleFileChange(e.target.files[0])}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          disabled={isUploading}
        />
        
        <div className="space-y-4">
          <div className="mx-auto w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center shadow-lg">
            <CloudArrowUpIcon className="w-8 h-8 text-white" />
          </div>
          
          <div>
            <h3 className="text-lg font-semibold text-slate-900 mb-2">
              {isUploading ? 'Processing...' : 'Drop your file here or click to browse'}
            </h3>
            <p className="text-slate-600">
              Supports .xlsx (Excel) and .txt (Tab-separated) files
            </p>
          </div>

          {/* Selected file indicator */}
          {selectedFile && !isUploading && (
            <div className="inline-flex items-center gap-3 px-4 py-2 rounded-xl bg-slate-100 border border-slate-200 text-slate-700 text-sm font-medium">
              <span className="truncate max-w-[32ch]">📄 {selectedFile.name}</span>
              <button
                type="button"
                className="text-emerald-700 font-semibold hover:underline"
                onClick={() => setSelectedFile(null)}
              >
                Remove
              </button>
            </div>
          )}
          
          {isUploading && (
            <div className="flex items-center justify-center space-x-2">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-emerald-600"></div>
              <span className="text-emerald-600 font-medium">Validating file...</span>
            </div>
          )}
        </div>
      </div>

      {/* Submit button */}
      <div className="mt-6 flex items-center justify-center">
        <button
          type="button"
          disabled={!selectedFile || !engagementDate || isUploading}
          onClick={() => processFile(selectedFile)}
          className={`px-6 py-3 rounded-xl font-semibold shadow-md transition-colors
            ${!selectedFile || !engagementDate || isUploading
              ? 'bg-slate-200 text-slate-500 cursor-not-allowed'
              : 'bg-emerald-600 hover:bg-emerald-700 text-white'}
          `}
        >
          {isUploading ? 'Submitting…' : 'Validate & Submit'}
        </button>
      </div>

      {/* Simplified UI – extra info removed per request */}
    </div>
  );
}